import {
    Overlay,
    OverlayModule,
    OverlayPositionBuilder,
    type OverlayRef,
} from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { ScrollDispatcher } from '@angular/cdk/scrolling'
import { isPlatformBrowser } from '@angular/common'
import {
    afterNextRender,
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    DestroyRef,
    ElementRef,
    effect,
    forwardRef,
    inject,
    Injector,
    input,
    model,
    type OnDestroy,
    output,
    PLATFORM_ID,
    runInInjectionContext,
    signal,
    type TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

import type { ClassValue } from 'clsx'
import { filter } from 'rxjs'

import {
    APP_DROPDOWN_OVERLAY_VIEWPORT_MARGIN,
    APP_FIXED_HEADER_HEIGHT_PX,
} from '@core/constants/app.constants'
import { REGEX_PATTERNS } from '@core/constants/regex.constants'
import { ZardIconComponent } from '@/styles/components/icon'
import { ZardSelectItemComponent } from '@/styles/components/select/select-item.component'
import {
    selectContentVariants,
    selectTriggerVariants,
    selectVariants,
    type ZardSelectSizeVariants,
} from '@/styles/components/select/select.variants'
import { mergeClasses } from '@/styles/utils/merge-classes'

type OnTouchedType = () => void
type OnChangeType = (value: string | string[]) => void

const COMPACT_MODE_WIDTH_THRESHOLD = 100
/** Reserve width for chevron icon and padding when computing how many labels fit */
const TRIGGER_RESERVE_WIDTH_PX = 52
type ZardSelectAppearance = 'default' | 'filter'

@Component({
    selector: 'z-select, [z-select]',
    imports: [OverlayModule, ZardIconComponent],
    styleUrl: './select.component.scss',
    template: `
        <button
            type="button"
            role="combobox"
            aria-controls="dropdown"
            [class]="triggerClasses()"
            [disabled]="isEffectivelyDisabled()"
            [attr.aria-expanded]="isOpen()"
            [attr.aria-haspopup]="'listbox'"
            [attr.data-placeholder]="!zValue() ? '' : null"
            (blur)="!isOpen() && isFocus.set(false)"
            (click)="toggle()"
            (focus)="onFocus()"
        >
            <span class="flex min-w-0 flex-1 items-center gap-2">
                @if (zMultiple()) {
                    @if (multiSelectTriggerPlainText()) {
                        <span
                            class="truncate text-left"
                            [attr.title]="multiSelectHoverTitle() || null"
                            >{{ multiSelectTriggerPlainText() }}</span
                        >
                    } @else if (zMultiselectChips()) {
                        @if (multiselectShowAllLabelOnly()) {
                            <span class="truncate text-left">{{ multiselectAllLabelText() }}</span>
                        } @else if (!multiselectChipValues().length) {
                            <span class="text-muted-foreground truncate">{{ zPlaceholder() }}</span>
                        } @else {
                            <span class="z-select-multiselect-chip-row">
                                @for (v of multiselectChipValues(); track v) {
                                    <span class="z-select-multiselect-chip">{{
                                        chipLabelForValue(v)
                                    }}</span>
                                }
                            </span>
                        }
                    } @else if (selectedLabelsDisplayText()) {
                        <span class="truncate text-left">{{ selectedLabelsDisplayText() }}</span>
                    } @else {
                        <span class="text-muted-foreground truncate">{{ zPlaceholder() }}</span>
                    }
                } @else {
                    @for (label of selectedLabels(); track label) {
                        <span class="truncate">{{ label }}</span>
                    } @empty {
                        <span class="text-muted-foreground truncate">{{ zPlaceholder() }}</span>
                    }
                }
            </span>
            <z-icon zType="chevron-down" zSize="lg" class="opacity-50" />
        </button>

        <ng-template #dropdownTemplate>
            <div
                id="dropdown"
                [class]="contentClasses()"
                role="listbox"
                [attr.data-state]="'open'"
                (keydown.{arrowdown,arrowup,enter,space,escape,home,end}.prevent)="
                    onDropdownKeydown($event)
                "
                tabindex="-1"
                style="width:100%;box-sizing:border-box;"
            >
                <div class="p-1" style="width:100%;box-sizing:border-box;">
                    <ng-content />
                </div>
            </div>
        </ng-template>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ZardSelectComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        /* Do not show "active" ring when disabled — closed selects set isFocus true in normal mode */
        '[attr.data-active]': 'hostDataActive()',
        '[attr.data-disabled]': 'hostDataDisabled()',
        '[attr.data-state]': 'isOpen() ? "open" : "closed"',
        '[class]': 'classes()',
        '(keydown.{enter,space,arrowdown,arrowup,escape}.prevent)': 'onTriggerKeydown($event)',
        '(document:click)': 'onDocumentClick($event)',
    },
})
export class ZardSelectComponent implements ControlValueAccessor, OnDestroy {
    private readonly destroyRef = inject(DestroyRef)
    private readonly elementRef = inject(ElementRef<HTMLElement>)
    private readonly injector = inject(Injector)
    private readonly overlay = inject(Overlay)
    private readonly overlayPositionBuilder = inject(OverlayPositionBuilder)
    private readonly scrollDispatcher = inject(ScrollDispatcher)
    private readonly viewContainerRef = inject(ViewContainerRef)
    private readonly platformId = inject(PLATFORM_ID)

    readonly dropdownTemplate = viewChild.required<TemplateRef<void>>('dropdownTemplate')
    readonly selectItems = contentChildren(ZardSelectItemComponent)

    private overlayRef?: OverlayRef
    private portal?: TemplatePortal

    readonly class = input<ClassValue>('')
    readonly zDisabled = input(false, { transform: booleanAttribute })
    readonly zLabel = input<string>('')
    /** Optional native tooltip for multi-select trigger (full list). If empty, derived from selected item labels when 2+ selected. */
    readonly zSelectionTooltip = input<string>('')
    /** When set (e.g. by ConfigurableDropdown), resolve trigger text from value keys when items are not matched yet. */
    readonly zValueLabelMap = input<Readonly<Record<string, string>> | null>(null)
    readonly zMaxLabelCount = input<number>(1)
    readonly zMultiple = input<boolean>(false)
    readonly zMultiselectSelectAllValue = input<string | null>(null)
    readonly zMultiselectChips = input(false, { transform: booleanAttribute })
    readonly zPanelMaxHeight = input<number>(384)
    readonly zPanelClass = input<ClassValue>('')
    readonly zPlaceholder = input<string>('Select an option...')
    /** When false, single-select cannot be cleared by re-selecting the current item. Default false = no deselect. */
    readonly zAllowDeselect = input<boolean>(false)
    /** When true, prefer opening the panel above the trigger (e.g. for selects near bottom of dialogs) */
    readonly zPreferUp = input<boolean>(false)
    readonly zSize = input<ZardSelectSizeVariants>('default')
    readonly zAppearance = input<ZardSelectAppearance>('default')
    readonly zValue = model<string | string[]>(this.zMultiple() ? [] : '')

    readonly zOpenChange = output<boolean>()
    readonly zSelectionChange = output<string | string[]>()

    readonly isOpen = signal(false)
    readonly focusedIndex = signal<number>(-1)
    protected readonly isFocus = signal(false)
    protected readonly isCompact = signal(false)
    private readonly triggerWidth = signal(0)
    /** When using formControlName without [zDisabled], Angular drives disabled via CVA */
    private readonly disabledFromForm = signal(false)

    protected readonly isEffectivelyDisabled = computed(
        () => this.zDisabled() || this.disabledFromForm(),
    )

    protected readonly hostDataActive = computed(() =>
        this.isFocus() && !this.isEffectivelyDisabled() ? '' : null,
    )

    protected readonly hostDataDisabled = computed(() => (this.isEffectivelyDisabled() ? '' : null))

    protected onFocus(): void {
        if (this.isEffectivelyDisabled()) {
            return
        }
        if (this.isCompact()) {
            this.isFocus.set(true)
        }
    }

    onDocumentClick(event: Event): void {
        if (!this.isOpen()) {
            return
        }

        const target = event.target as Node | null
        if (!target) {
            return
        }

        // Click on the trigger itself
        if (this.elementRef.nativeElement.contains(target)) {
            return
        }

        // Click inside the overlay content (select items)
        if (this.overlayRef?.overlayElement?.contains(target)) {
            return
        }

        this.isFocus.set(false)
        this.close()
    }

    // Compute the label based on selected value
    readonly selectedLabels = computed<string[]>(() => {
        // Always subscribe to label inputs so the trigger updates when options/map arrive
        // without requiring zValue() to change again (e.g. Call Status after remount).
        this.zLabel()
        this.zValueLabelMap()

        const selectedValue = this.zValue()
        if (this.zMultiple() && Array.isArray(selectedValue)) {
            return this.provideLabelsForMultiselectMode(selectedValue)
        }

        return this.provideLabelForSingleSelectMode(selectedValue as string)
    })

    /** Multi-select: items shown based on trigger width; CSS truncate for overflow */
    readonly selectedLabelsDisplayText = computed<string>(() => {
        if (!this.zMultiple()) return ''
        const selectedValue = this.zValue()
        if (!Array.isArray(selectedValue) || selectedValue.length === 0) return ''
        const labels = this.provideAllLabelsForMultiselectMode(selectedValue)
        const maxCount = this.getMaxLabelsToShow(labels)
        if (maxCount >= labels.length) return labels.join(', ')
        const rest = labels.length - maxCount
        return labels.slice(0, maxCount).join(', ') + ` +${rest}`
    })

    /** Multi-select trigger text: prefer parent-provided zLabel (e.g. "Name (Role) +2"), else width-aware summary. */
    readonly multiSelectTriggerPlainText = computed(() => {
        if (!this.zMultiple()) return ''
        const selectedValue = this.zValue()
        if (!Array.isArray(selectedValue) || selectedValue.length === 0) return ''
        const custom = this.zLabel()?.trim()
        if (custom) return custom
        return this.selectedLabelsDisplayText()
    })

    /** Native title on multi-select trigger: custom tooltip or all selected labels when 2+. */
    readonly multiSelectHoverTitle = computed(() => {
        if (!this.zMultiple()) return ''
        const custom = this.zSelectionTooltip()?.trim()
        if (custom) return custom
        const selectedValue = this.zValue()
        if (!Array.isArray(selectedValue) || selectedValue.length < 2) return ''
        const labels = this.provideAllLabelsForMultiselectMode(selectedValue)
        return labels.length > 1 ? labels.join(' • ') : ''
    })

    readonly multiselectShowAllLabelOnly = computed(() => {
        if (!this.zMultiple() || !this.zMultiselectChips()) return false
        const selected = this.zValue()
        if (!Array.isArray(selected) || selected.length !== 1) return false
        return selected[0] === 'all'
    })

    readonly multiselectChipValues = computed(() => {
        if (!this.zMultiple() || !this.zMultiselectChips()) return [] as string[]
        const selected = this.zValue()
        if (!Array.isArray(selected) || selected.length === 0) return []
        if (selected.length === 1 && selected[0] === 'all') return []
        return selected.filter((v) => v !== 'all').map(String)
    })

    readonly multiselectAllLabelText = computed(() => {
        this.selectItems()
        const item = this.getMatchingItem('all')
        return item?.label() ?? 'All'
    })

    private onChange: OnChangeType = (_value: string | string[]) => {
        // ControlValueAccessor onChange callback
    }

    private onTouched: OnTouchedType = () => {
        // ControlValueAccessor onTouched callback
    }

    protected readonly classes = computed(() => mergeClasses(selectVariants(), this.class()))
    protected readonly contentClasses = computed(() =>
        mergeClasses(selectContentVariants(), this.zPanelClass()),
    )
    protected readonly triggerClasses = computed(() =>
        mergeClasses(
            selectTriggerVariants({
                zAppearance: this.zAppearance(),
                zSize: this.zSize(),
            }),
        ),
    )

    constructor() {
        afterNextRender(
            () => {
                if (!isPlatformBrowser(this.platformId)) return
                const el = this.elementRef.nativeElement
                const ro = new ResizeObserver(() => {
                    this.triggerWidth.set(el.offsetWidth || 0)
                })
                ro.observe(el)
                this.destroyRef.onDestroy(() => ro.disconnect())
                this.triggerWidth.set(el.offsetWidth || 0)
            },
            { injector: this.injector },
        )

        // Keep item wiring reactive so async/late-rendered options are always clickable.
        effect(() => {
            const items = this.selectItems()
            const hostWidth = this.elementRef.nativeElement.offsetWidth || 0
            const compactMode = hostWidth <= COMPACT_MODE_WIDTH_THRESHOLD

            this.isCompact.set(compactMode)

            const selectAllSentinel = this.zMultiselectSelectAllValue()
            const useSelectAllDerived =
                this.zMultiple() && selectAllSentinel != null && selectAllSentinel !== ''

            for (let i = 0; i < items.length; i++) {
                const item = items[i]
                item.setSelectHost({
                    selectedValue: () => {
                        const raw = this.zValue()
                        if (this.zMultiple()) {
                            if (!Array.isArray(raw)) return []
                            return raw.map(String)
                        }
                        if (raw === null || raw === undefined || raw === '') return []
                        return [String(raw)]
                    },
                    selectItem: (value: string, label: string) => this.selectItem(value, label),
                    navigateTo: () => this.navigateTo(item, i),
                    isMultiple: () => this.zMultiple(),
                    isMultiselectItemSelected: useSelectAllDerived
                        ? (value: string) =>
                              this.isMultiselectOptionSelected(value, selectAllSentinel)
                        : undefined,
                })
                item.zSize.set(this.zSize())
                item.zMode.set(compactMode ? 'compact' : 'normal')
            }
        })

        this.scrollDispatcher
            .scrolled(0)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isOpen()) {
                    this.updateOverlayHeaderClip()
                }
            })
    }

    ngOnDestroy() {
        this.destroyOverlay()
    }

    onTriggerKeydown(event: Event) {
        if (this.isEffectivelyDisabled()) {
            return
        }
        const { key } = event as KeyboardEvent
        switch (key) {
            case 'Enter':
            case ' ':
            case 'ArrowDown':
            case 'ArrowUp':
                if (!this.isOpen()) {
                    this.open()
                }
                break
            case 'Escape':
                if (this.isOpen()) {
                    this.close()
                }
                break
        }
    }

    onDropdownKeydown(e: Event) {
        const { key } = e as KeyboardEvent
        const items = this.getSelectItems()

        switch (key) {
            case 'ArrowDown':
                this.navigateItems(1, items)
                break
            case 'ArrowUp':
                this.navigateItems(-1, items)
                break
            case 'Enter':
            case ' ':
                this.selectFocusedItem(items)
                break
            case 'Escape':
                this.close()
                this.focusButton()
                break
            case 'Home':
                this.focusFirstItem(items)
                break
            case 'End':
                this.focusLastItem(items)
                break
        }
    }

    toggle() {
        if (this.isEffectivelyDisabled()) {
            return
        }

        if (this.isOpen()) {
            this.close()
        } else {
            this.open()
        }
    }

    private isMultiselectOptionSelected(itemValue: string, selectAllSentinel: string): boolean {
        const raw = this.zValue()
        if (!this.zMultiple() || !Array.isArray(raw)) return false
        const normalized = raw.map(String)
        if (itemValue === selectAllSentinel) {
            if (normalized.includes(itemValue)) return true
            return this.areAllMultiselectOptionsExceptSelectAllSelected(
                normalized,
                selectAllSentinel,
            )
        }
        return normalized.includes(itemValue)
    }

    private areAllMultiselectOptionsExceptSelectAllSelected(
        selected: string[],
        selectAllSentinel: string,
    ): boolean {
        const optionValues = (this.selectItems() ?? [])
            .map((item) => String(item.zValue()))
            .filter((val) => val !== selectAllSentinel)
        return optionValues.length > 0 && optionValues.every((val) => selected.includes(val))
    }

    selectItem(value: string, _label: string) {
        // Allow empty-string values (e.g. "Select Source" placeholder) so users can
        // clear a selection by choosing the first option again. Only block truly
        // missing values (undefined / null).
        if (value === undefined || value === null) {
            return
        }

        this.zValue.update((selectedValues) => {
            if (Array.isArray(selectedValues)) {
                // Multi-select: toggle item on/off.
                return selectedValues.includes(value)
                    ? selectedValues.filter((v) => v !== value)
                    : [...selectedValues, value]
            }

            const current = selectedValues as string
            // Single-select: clicking the same option again clears the value
            // (used to "deselect" Source, Designation, etc.).
            if (current === value && !this.zAllowDeselect()) {
                return current
            }
            return current === value ? '' : value
        })
        this.onChange(this.zValue())
        this.zSelectionChange.emit(this.zValue())

        if (this.zMultiple()) {
            // in multiple mode it can happen that button changes size because of selection badges,
            // which requires overlay position to update
            this.updateOverlayPosition()
        } else {
            this.close()

            // Return focus to the button after selection
            setTimeout(() => {
                this.focusButton()
            }, 0)
        }
    }

    private navigateTo(element: ZardSelectItemComponent, index: number): void {
        this.focusedIndex.set(index)
        this.updateItemFocus(this.getSelectItems(true), index)
    }

    private updateOverlayPosition(): void {
        setTimeout(() => {
            this.overlayRef?.updatePosition()
            this.updateOverlayHeaderClip()
        }, 0)
    }

    private provideLabelsForMultiselectMode(selectedValue: string[]): string[] {
        const labelsToShowCount = selectedValue.length - this.zMaxLabelCount()
        const labels = []
        let index = 0
        for (const value of selectedValue) {
            const matchingItem = this.getMatchingItem(value)
            if (matchingItem) {
                labels.push(matchingItem.label())
                index++
            }
            if (labelsToShowCount && this.zMaxLabelCount() && index === this.zMaxLabelCount()) {
                labels.push('...')
                break
            }
        }
        return labels
    }

    /** Returns all selected labels (no truncation) for comma-separated display */
    private provideAllLabelsForMultiselectMode(selectedValue: string[]): string[] {
        const labels: string[] = []
        for (const value of selectedValue) {
            const matchingItem = this.getMatchingItem(value)
            if (matchingItem) {
                labels.push(matchingItem.label())
            }
        }
        return labels
    }

    /** How many labels fit in the trigger; zMaxLabelCount is ceiling. */
    private getMaxLabelsToShow(labels: string[]): number {
        const ceiling = this.zMaxLabelCount()
        if (!isPlatformBrowser(this.platformId)) {
            return ceiling && ceiling > 0 ? Math.min(labels.length, ceiling) : labels.length
        }
        const availableWidth = Math.max(0, this.triggerWidth() - TRIGGER_RESERVE_WIDTH_PX)
        if (availableWidth <= 0) return Math.min(1, labels.length)
        const measure = (t: string) => this.measureTextWidth(t)
        let prefix = ''
        for (let i = 0; i < labels.length; i++) {
            const nextPrefix = i === 0 ? labels[i] : prefix + ', ' + labels[i]
            const remaining = labels.length - i - 1
            const candidate = remaining > 0 ? `${nextPrefix} +${remaining}` : nextPrefix
            if (measure(candidate) > availableWidth) {
                const count = Math.max(1, i)
                return ceiling && ceiling > 0 ? Math.min(count, ceiling) : count
            }
            prefix = nextPrefix
        }
        return ceiling && ceiling > 0 ? Math.min(labels.length, ceiling) : labels.length
    }

    private measureTextWidth(text: string): number {
        if (!isPlatformBrowser(this.platformId) || !text) return 0
        const el = this.elementRef.nativeElement
        const span = document.createElement('span')
        span.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font:inherit;'
        span.textContent = text
        const style = getComputedStyle(el)
        span.style.font = style.font || `${style.fontSize} ${style.fontFamily}`
        document.body.appendChild(span)
        const w = span.offsetWidth
        document.body.removeChild(span)
        return w
    }

    private provideLabelForSingleSelectMode(selectedValue: string): string[] {
        const needle = String(selectedValue ?? '').trim()
        if (!needle) return []

        // Prefer map first: stable label while the overlay is closed (items not in DOM) and
        // when zLabel() lags behind option load; avoids showing raw id until a second click.
        const map = this.zValueLabelMap()
        const fromMap = map?.[needle]?.trim()
        if (fromMap) {
            return [fromMap]
        }

        const manualLabel = this.zLabel()?.trim()
        if (manualLabel) {
            return [manualLabel]
        }

        const matchingItem = this.getMatchingItem(selectedValue)
        if (matchingItem) {
            return [matchingItem.label()]
        }

        return []
    }

    private open() {
        if (this.isEffectivelyDisabled()) {
            return
        }
        if (this.isOpen()) {
            return
        }

        // Create overlay if it doesn't exist
        if (!this.overlayRef) {
            this.createOverlay()
        }

        if (!this.overlayRef) {
            return
        }

        const hostWidth = this.elementRef.nativeElement.offsetWidth || 0

        if (this.overlayRef.hasAttached()) {
            this.overlayRef.detach()
        }

        this.portal = new TemplatePortal(this.dropdownTemplate(), this.viewContainerRef)

        this.overlayRef.attach(this.portal)
        // Always enforce trigger width as the minimum — panel must never be narrower
        this.overlayRef.updateSize({
            minWidth: hostWidth,
            width: hostWidth,
            maxHeight: this.zPanelMaxHeight(),
        })
        this.updateOverlayHeaderClip()
        this.isOpen.set(true)
        this.zOpenChange.emit(true)
        this.updateFocusWhenNormalMode()

        this.determinePortalWidthOnOpen(hostWidth)

        runInInjectionContext(this.injector, () => {
            afterNextRender(() => {
                if (this.overlayRef?.hasAttached() && this.getSelectItems(true).length === 0) {
                    this.close()
                }
            })
        })
    }

    private setFocusOnOpen(): void {
        this.focusDropdown()
        this.focusSelectedItem()
    }

    private close() {
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef.detach()
        }
        this.clearOverlayHeaderClip()
        this.isOpen.set(false)
        this.zOpenChange.emit(false)
        this.focusedIndex.set(-1)
        this.onTouched()
        this.updateFocusWhenNormalMode()
    }

    private updateFocusWhenNormalMode(): void {
        if (!this.isCompact()) {
            if (this.isEffectivelyDisabled()) {
                this.isFocus.set(false)
                return
            }
            this.isFocus.set(!this.isOpen())
        }
    }

    private getMatchingItem(value: string): ZardSelectItemComponent | undefined {
        const needle = value == null ? '' : String(value).trim()
        if (needle === '') return undefined
        return this.selectItems()?.find((item) => String(item.zValue()).trim() === needle)
    }

    protected chipLabelForValue(value: string): string {
        const normalizedValue = String(value)
        const stageOptionMatch = normalizedValue.match(REGEX_PATTERNS.STAGE_OPTION_VALUE)
        if (stageOptionMatch) {
            const stageNumber = stageOptionMatch[1]
            const stageOptionItem = this.getMatchingItem(normalizedValue)

            switch (stageNumber) {
                case '6':
                    return stageOptionItem ? stageOptionItem.label() : normalizedValue
                case '7':
                    return stageOptionItem ? stageOptionItem.label() : normalizedValue
                default:
                    return `S${stageNumber}`
            }
        }
        const matchingItem = this.getMatchingItem(normalizedValue)
        return matchingItem ? matchingItem.label() : normalizedValue
    }

    private determinePortalWidthOnOpen(triggerWidth: number): void {
        runInInjectionContext(this.injector, () => {
            afterNextRender(() => {
                if (!this.overlayRef || !this.overlayRef.hasAttached()) {
                    return
                }

                // Always start from the trigger width — the panel must never be narrower than the trigger.
                // Only expand beyond it if any option text overflows.
                let finalWidth = triggerWidth

                const overlayPaneElement = this.overlayRef.overlayElement
                const textElements = Array.from(
                    overlayPaneElement.querySelectorAll<HTMLElement>(
                        'z-select-item > span.truncate, [z-select-item] > span.truncate',
                    ),
                )

                for (const textElement of textElements) {
                    if (textElement.scrollWidth > textElement.clientWidth + 1) {
                        // Text overflows — measure the widest item and use that if it's wider
                        const selectItems = this.selectItems()
                        for (const item of selectItems) {
                            const itemEl = item.elementRef.nativeElement
                            const styles = getComputedStyle(itemEl)
                            const paddingH =
                                Number.parseFloat(styles.paddingLeft || '0') +
                                Number.parseFloat(styles.paddingRight || '0')
                            finalWidth = Math.max(finalWidth, itemEl.scrollWidth + paddingH)
                        }
                        break
                    }
                }

                this.overlayRef.updateSize({
                    minWidth: triggerWidth,
                    width: finalWidth,
                    maxHeight: this.zPanelMaxHeight(),
                })
                this.overlayRef.updatePosition()
                this.updateOverlayHeaderClip()
                this.setFocusOnOpen()
            })
        })
    }

    private updateOverlayHeaderClip(): void {
        if (!isPlatformBrowser(this.platformId) || !this.overlayRef?.hasAttached()) {
            return
        }

        requestAnimationFrame(() => {
            if (!this.overlayRef?.hasAttached()) {
                return
            }

            const overlayPanelElement = this.getOverlayPanelElement()
            if (!overlayPanelElement) {
                return
            }

            const panelTop = overlayPanelElement.getBoundingClientRect().top
            const headerOverlap = Math.max(0, APP_FIXED_HEADER_HEIGHT_PX - panelTop)

            if (headerOverlap <= 0) {
                this.clearOverlayHeaderClip()
                return
            }

            const clipPathValue = `inset(${headerOverlap}px 0 0 0)`
            overlayPanelElement.style.clipPath = clipPathValue
            overlayPanelElement.style.setProperty('-webkit-clip-path', clipPathValue)
        })
    }

    private clearOverlayHeaderClip(): void {
        const overlayPanelElement = this.getOverlayPanelElement()
        overlayPanelElement?.style.removeProperty('clip-path')
        overlayPanelElement?.style.removeProperty('-webkit-clip-path')
    }

    private getOverlayPanelElement(): HTMLElement | null {
        if (!this.overlayRef) {
            return null
        }

        return (
            this.overlayRef.overlayElement.querySelector<HTMLElement>('[role="listbox"]') ??
            this.overlayRef.overlayElement
        )
    }

    private createOverlay() {
        if (this.overlayRef) {
            return
        } // Already created

        if (isPlatformBrowser(this.platformId)) {
            try {
                const preferUp = this.zPreferUp()
                const positions = preferUp
                    ? [
                          {
                              originX: 'start' as const,
                              originY: 'top' as const,
                              overlayX: 'start' as const,
                              overlayY: 'bottom' as const,
                              offsetY: -4,
                          },
                          {
                              originX: 'start' as const,
                              originY: 'bottom' as const,
                              overlayX: 'start' as const,
                              overlayY: 'top' as const,
                              offsetY: 4,
                          },
                      ]
                    : [
                          {
                              originX: 'start' as const,
                              originY: 'bottom' as const,
                              overlayX: 'start' as const,
                              overlayY: 'top' as const,
                              offsetY: 4,
                          },
                          {
                              originX: 'start' as const,
                              originY: 'top' as const,
                              overlayX: 'start' as const,
                              overlayY: 'bottom' as const,
                              offsetY: -4,
                          },
                      ]
                const positionStrategy = this.overlayPositionBuilder
                    .flexibleConnectedTo(this.elementRef)
                    .withPositions(positions)
                    /* Stay locked to the trigger; `withPush(true)` slides the panel into the top
                     * “safe” viewport band so it reads as glued to the header instead of the combobox. */
                    .withPush(false)
                    .withViewportMargin(APP_DROPDOWN_OVERLAY_VIEWPORT_MARGIN)
                    .withFlexibleDimensions(false)

                const elementWidth = this.elementRef.nativeElement.offsetWidth || 200

                this.overlayRef = this.overlay.create({
                    positionStrategy,
                    hasBackdrop: false,
                    // Reposition with the trigger when scrollable ancestors scroll.
                    // Nested overflow areas must use `cdkScrollable` so the CDK scroll
                    // dispatcher emits (see generate-proposal main column).
                    scrollStrategy: this.overlay.scrollStrategies.reposition(),
                    minWidth: elementWidth,
                    width: elementWidth,
                    maxHeight: this.zPanelMaxHeight(),
                })
                positionStrategy.positionChanges
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => this.updateOverlayHeaderClip())
                this.overlayRef
                    .outsidePointerEvents()
                    .pipe(
                        filter((event) => !this.elementRef.nativeElement.contains(event.target)),
                        takeUntilDestroyed(this.destroyRef),
                    )
                    .subscribe(() => {
                        this.isFocus.set(false)
                        this.close()
                    })
            } catch (error) {
                console.error('Error creating overlay:', error)
            }
        }
    }

    private destroyOverlay() {
        if (this.overlayRef) {
            this.overlayRef.dispose()
            this.overlayRef = undefined
        }
    }

    private getSelectItems(ignoreFilter = false): HTMLElement[] {
        if (!this.overlayRef?.hasAttached()) {
            return []
        }
        const dropdownElement = this.overlayRef.overlayElement
        return Array.from(
            dropdownElement.querySelectorAll<HTMLElement>('z-select-item, [z-select-item]'),
        ).filter((item) => ignoreFilter || item.dataset['disabled'] === undefined)
    }

    private navigateItems(direction: number, items: HTMLElement[]) {
        if (items.length === 0) {
            return
        }

        const currentIndex = this.focusedIndex()
        let nextIndex = currentIndex + direction

        if (nextIndex < 0) {
            nextIndex = items.length - 1
        } else if (nextIndex >= items.length) {
            nextIndex = 0
        }

        this.focusedIndex.set(nextIndex)
        this.updateItemFocus(items, nextIndex)
    }

    private selectFocusedItem(items: HTMLElement[]) {
        const currentIndex = this.focusedIndex()
        if (currentIndex >= 0 && currentIndex < items.length) {
            const item = items[currentIndex]
            const value = item.getAttribute('value')
            const label = item.textContent?.trim() ?? ''

            if (value === null || value === undefined) {
                console.warn('No value attribute found on selected item:', item)
                return
            }

            this.selectItem(value, label)
        }
    }

    private focusFirstItem(items: HTMLElement[]) {
        if (items.length > 0) {
            this.focusedIndex.set(0)
            this.updateItemFocus(items, 0)
        }
    }

    private focusLastItem(items: HTMLElement[]) {
        if (items.length > 0) {
            const lastIndex = items.length - 1
            this.focusedIndex.set(lastIndex)
            this.updateItemFocus(items, lastIndex)
        }
    }

    private updateItemFocus(items: HTMLElement[], focusedIndex: number) {
        for (let index = 0; index < items.length; index++) {
            const item = items[index]
            if (index === focusedIndex) {
                item.focus()
                item.setAttribute('data-focused', 'true')
            } else {
                item.removeAttribute('data-focused')
            }
        }
    }

    private focusDropdown() {
        if (this.overlayRef?.hasAttached()) {
            const dropdownElement = this.overlayRef.overlayElement.querySelector(
                '[role="listbox"]',
            ) as HTMLElement
            if (dropdownElement) {
                dropdownElement.focus()
            }
        }
    }

    private focusButton() {
        const button = this.elementRef.nativeElement.querySelector('button')
        if (button) {
            button.focus()
        }
    }

    private focusSelectedItem() {
        const items = this.getSelectItems()
        if (items.length === 0) {
            return
        }

        // Find the index of the currently selected item
        let selectedValue
        if (Array.isArray(this.zValue()) && this.zValue().length) {
            ;[selectedValue] = this.zValue()
        } else {
            selectedValue = this.zValue()
        }

        let selectedIndex = items.findIndex(
            (item) => String(item.getAttribute('value') ?? '') === String(selectedValue ?? ''),
        )

        // If no item is selected, focus the first item
        if (selectedIndex === -1) {
            selectedIndex = 0
        }

        this.focusedIndex.set(selectedIndex)
        this.updateItemFocus(items, selectedIndex)
    }

    // ControlValueAccessor implementation
    writeValue(value: string | string[] | null): void {
        if (this.zMultiple() && Array.isArray(value)) {
            this.zValue.set(value)
        } else {
            this.zValue.set(value ?? '')
        }
    }

    registerOnChange(fn: (value: string | string[]) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabledFromForm.set(isDisabled)
        if (isDisabled) {
            this.isFocus.set(false)
            this.close()
        }
    }
}
